<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-baby-face me-2"></i>{{ $t('page.nicu', 'NICU / Neonatal') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openAdmitModal()"><i class="mdi mdi-plus"></i> Admit Baby</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.in_unit || 0 }}</div><div class="hms-stat-lbl">In Unit</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.very_preterm || 0 }}</div><div class="hms-stat-lbl">Very/Ext Preterm</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.vlbw_elbw || 0 }}</div><div class="hms-stat-lbl">VLBW / ELBW</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.level3 || 0 }}</div><div class="hms-stat-lbl">Level 3 Care</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.discharged_today || 0 }}</div><div class="hms-stat-lbl">Discharged Today</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='census' }" @click="tab='census'"><i class="mdi mdi-clipboard-list me-1"></i>Census</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }" @click="tab='detail'" :disabled="!detail.admission"><i class="mdi mdi-card-account-details me-1"></i>Baby Card</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>NICU Indicators</button>
      </div>

      <!-- ============ CENSUS ============ -->
      <div v-if="tab==='census'">
        <div v-if="!occupancy.length" class="hms-empty">
          <i class="mdi mdi-baby-bottle-outline"></i><h5>No babies in NICU</h5>
        </div>
        <div v-else class="hms-baby-grid">
          <div v-for="b in occupancy" :key="b.nicu_admission_id" class="hms-baby-card" :class="cardClass(b)">
            <div class="hms-baby-head">
              <div>
                <div class="hms-baby-name">{{ b.baby_name }}</div>
                <div class="hms-muted-sm">{{ b.nicu_number }} · {{ b.patient_no }}</div>
              </div>
              <span class="hms-pill" :class="careLevelPill(b.care_level)">{{ formatCareLevel(b.care_level) }}</span>
            </div>
            <div class="hms-baby-meta">
              <div><span class="hms-vk">DOL</span> {{ b.day_of_life || '?' }}</div>
              <div><span class="hms-vk">DIN</span> {{ Math.floor((b.hours_in_nicu || 0) / 24) + 1 }}</div>
              <div><span class="hms-vk">Wt</span> {{ b.latest_weight_g || b.admission_weight_g || '—' }}g</div>
              <div><span class="hms-vk">GA</span> {{ b.gestation_weeks || '—' }}w</div>
            </div>
            <div class="hms-baby-tags">
              <span v-if="b.prematurity_class !== 'term'" class="hms-tag hms-tag-warn">{{ formatPrem(b.prematurity_class) }}</span>
              <span v-if="['very_low','extremely_low'].includes(b.birth_weight_class)" class="hms-tag hms-tag-warn">{{ formatBw(b.birth_weight_class) }}</span>
              <span v-if="b.phototx_24h == 1" class="hms-tag hms-tag-info"><i class="mdi mdi-lightbulb-on"></i> Photo</span>
              <span v-if="b.o2_24h == 1" class="hms-tag hms-tag-info"><i class="mdi mdi-air-filter"></i> O₂</span>
              <span v-if="b.cpap_24h == 1" class="hms-tag hms-tag-warn">CPAP</span>
              <span v-if="b.ever_sepsis == 1" class="hms-tag hms-tag-danger">Sepsis</span>
            </div>
            <div v-if="b.admission_diagnosis" class="hms-baby-dx">{{ b.admission_diagnosis }}</div>
            <div v-if="b.mother_name" class="hms-muted-sm">Mother: {{ b.mother_name }}<span v-if="b.mother_phone"> · {{ b.mother_phone }}</span></div>
            <div class="hms-muted-sm" v-if="b.last_assessed">Last note: {{ formatRelative(b.last_assessed) }}</div>
            <div class="hms-baby-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(b)"><i class="mdi mdi-folder-open"></i> Open</button>
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openProgressFor(b)"><i class="mdi mdi-clipboard-text"></i> Note</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ BABY CARD ============ -->
      <div v-if="tab==='detail' && detail.admission">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.admission.baby_name }}</div>
                <div class="hms-muted-sm">{{ detail.admission.nicu_number }} · {{ detail.admission.patient_no }} · {{ detail.admission.gender || '—' }} · DOB {{ detail.admission.dob }}</div>
                <div class="hms-muted-sm" v-if="detail.admission.mother_name">Mother: {{ detail.admission.mother_name }} <span v-if="detail.admission.mother_phone">({{ detail.admission.mother_phone }})</span></div>
                <div v-if="detail.admission.admission_diagnosis" class="hms-muted-sm">Dx: {{ detail.admission.admission_diagnosis }}</div>
              </div>
              <div class="text-end">
                <div><strong>Cot {{ detail.admission.cot_number || '—' }}</strong> · <span class="hms-pill" :class="careLevelPill(detail.admission.care_level)">{{ formatCareLevel(detail.admission.care_level) }}</span></div>
                <div class="hms-muted-sm">Admitted {{ detail.admission.admission_date }}</div>
                <div class="hms-muted-sm">{{ formatPrem(detail.admission.prematurity_class) }} · {{ formatBw(detail.admission.birth_weight_class) }}</div>
                <div class="hms-muted-sm">Birth wt: {{ detail.admission.admission_weight_g || '—' }}g · GA {{ detail.admission.gestation_weeks || '—' }}w</div>
                <div class="hms-muted-sm">APGAR {{ detail.admission.apgar_1 || '—' }}/{{ detail.admission.apgar_5 || '—' }}</div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openProgressModal()"><i class="mdi mdi-clipboard-text"></i> Add Progress Note</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openDischargeModal()"><i class="mdi mdi-flag-checkered"></i> Discharge / Outcome</button>
            </div>
          </div>
        </div>

        <!-- WEIGHT TREND CHART -->
        <div class="hms-card mb-3" v-if="weightPoints.length">
          <div class="hms-card-head">Weight trend</div>
          <div class="hms-card-body">
            <svg viewBox="0 0 660 200" style="width:100%; max-width:780px; background:#fff;">
              <g v-for="g in weightYTicks" :key="'y'+g">
                <line :x1="40" :y1="yWt(g)" :x2="640" :y2="yWt(g)" stroke="#edf2f7" stroke-width="1" />
                <text :x="34" :y="yWt(g) + 4" font-size="10" text-anchor="end" fill="#718096">{{ g }}g</text>
              </g>
              <line v-if="detail.admission.admission_weight_g" :x1="40" :y1="yWt(detail.admission.admission_weight_g)" :x2="640" :y2="yWt(detail.admission.admission_weight_g)" stroke="#a0aec0" stroke-width="1" stroke-dasharray="4,3" />
              <text v-if="detail.admission.admission_weight_g" :x="46" :y="yWt(detail.admission.admission_weight_g) - 4" font-size="9" fill="#a0aec0">birth</text>
              <polyline v-if="weightPoints.length > 1" :points="weightPoints.map(p => xDol(p.dol) + ',' + yWt(p.w)).join(' ')" fill="none" stroke="#2b6cb0" stroke-width="2" />
              <g v-for="(p, i) in weightPoints" :key="'wt'+i">
                <circle :cx="xDol(p.dol)" :cy="yWt(p.w)" r="4" fill="#2b6cb0" stroke="#fff" stroke-width="1.5" />
                <text :x="xDol(p.dol)" :y="yWt(p.w) - 8" font-size="9" text-anchor="middle" fill="#2c5282">{{ p.w }}</text>
              </g>
              <text x="20" y="100" font-size="10" fill="#4a5568" transform="rotate(-90 20 100)">Weight (g)</text>
              <text x="340" y="195" font-size="10" fill="#4a5568">Day of life</text>
            </svg>
          </div>
        </div>

        <!-- PROGRESS TABLE -->
        <div class="hms-card">
          <div class="hms-card-head">Progress notes ({{ detail.progress.length }})</div>
          <div class="hms-card-body p-0">
            <div v-if="!detail.progress.length" class="hms-muted text-center hms-pad">No progress notes yet.</div>
            <table v-else class="hms-table">
              <thead>
                <tr>
                  <th>When</th><th>DOL/DIN</th><th>Wt</th><th>T°</th><th>HR</th><th>RR</th><th>SpO₂</th>
                  <th>Feeding</th><th>Resp</th><th>Bili</th><th>KMC</th><th>Notes</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in detail.progress" :key="p.progress_id" :class="progressRowClass(p)">
                  <td>{{ p.assessed_at }}</td>
                  <td>{{ p.day_of_life }}/{{ p.day_in_nicu }}</td>
                  <td>{{ p.weight_g || '—' }}</td>
                  <td>{{ p.temperature || '—' }}</td>
                  <td>{{ p.heart_rate || '—' }}</td>
                  <td>{{ p.respiratory_rate || '—' }}</td>
                  <td>{{ p.spo2 != null ? p.spo2 + '%' : '—' }}</td>
                  <td>{{ formatFeeding(p.feeding_method) }} {{ p.feed_volume_ml ? p.feed_volume_ml + 'ml' : '' }}</td>
                  <td>
                    <span v-if="p.on_ventilator == 1" class="hms-tag hms-tag-danger">Vent</span>
                    <span v-else-if="p.on_cpap == 1" class="hms-tag hms-tag-warn">CPAP</span>
                    <span v-else-if="p.on_oxygen == 1" class="hms-tag hms-tag-info">O₂ {{ p.oxygen_lpm ? p.oxygen_lpm + 'L' : '' }}</span>
                    <span v-else class="hms-muted-sm">RA</span>
                  </td>
                  <td>
                    <span v-if="p.bilirubin_total != null">{{ p.bilirubin_total }}</span>
                    <span v-if="p.on_phototherapy == 1" class="hms-tag hms-tag-info"><i class="mdi mdi-lightbulb-on"></i></span>
                  </td>
                  <td>{{ p.kmc_hours || 0 }}h</td>
                  <td>{{ truncate(p.assessment_notes, 60) }}</td>
                  <td>{{ p.recorded_by_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ============ RESPIRATORY SUPPORT ============ -->
          <div class="hms-card mt-3">
            <div class="hms-card-head">
              <span><i class="mdi mdi-lungs"></i> Respiratory support</span>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openResp"><i class="mdi mdi-plus"></i> Record</button>
            </div>
            <div class="hms-card-body">
              <div class="hms-muted-sm mb-2">How the baby is helped to breathe — from room air to mechanical ventilation.</div>
              <table class="hms-table" v-if="(detail.Respiratory || []).length">
                <thead><tr><th>When</th><th>Mode</th><th>FiO2</th><th>Flow</th><th>PEEP/PIP</th><th>SpO2</th><th>WOB</th><th>ABG (pH / pCO2)</th><th>Surfactant</th></tr></thead>
                <tbody>
                  <tr v-for="r in detail.Respiratory" :key="r.rs_id" :class="r.support_mode === 'intubated' || r.work_of_breathing === 'severe' ? 'row-action' : ''">
                    <td>{{ formatDt(r.recorded_at) }}</td>
                    <td><span class="hms-pill" :class="respModePill(r.support_mode)">{{ r.support_mode.replace(/_/g,' ') }}</span></td>
                    <td>{{ r.fio2_pct ? r.fio2_pct + '%' : '—' }}</td>
                    <td>{{ r.flow_lpm ? r.flow_lpm + ' L/min' : '—' }}</td>
                    <td class="hms-muted-sm">{{ r.peep_cmH2O ? r.peep_cmH2O + '/' + (r.pip_cmH2O || '?') : '—' }}</td>
                    <td>{{ r.spo2_pct ? r.spo2_pct + '%' : '—' }}</td>
                    <td>{{ r.work_of_breathing }}</td>
                    <td class="hms-muted-sm">{{ r.abg_ph ? r.abg_ph + ' / ' + r.abg_pco2 : '—' }}</td>
                    <td><i v-if="r.surfactant_given==1" class="mdi mdi-check-circle hms-good"></i></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-muted">No respiratory support recorded yet.</div>
            </div>
          </div>

          <!-- ============ SEPSIS SCREEN ============ -->
          <div class="hms-card mt-3">
            <div class="hms-card-head">
              <span><i class="mdi mdi-bug"></i> Newborn Sepsis Screen</span>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openSepsis"><i class="mdi mdi-plus"></i> New screen</button>
            </div>
            <div class="hms-card-body">
              <div class="hms-muted-sm mb-2">Sepsis is the leading cause of newborn death in Kenya. Risk score + early antibiotics save lives.</div>
              <table class="hms-table" v-if="(detail.Sepsis || []).length">
                <thead><tr><th>When</th><th>DOL</th><th>Onset</th><th>Score</th><th>Classification</th><th>Antibiotic protocol</th><th>Culture</th></tr></thead>
                <tbody>
                  <tr v-for="s in detail.Sepsis" :key="s.ss_id" :class="s.classification === 'confirmed_sepsis' ? 'row-action' : (s.classification === 'suspected_sepsis' ? 'row-warn' : '')">
                    <td>{{ formatDt(s.screened_at) }}</td>
                    <td>{{ s.day_of_life || '—' }}</td>
                    <td>{{ s.sepsis_onset_type.replace(/_/g,' ') }}</td>
                    <td><strong>{{ s.risk_score }}</strong></td>
                    <td><span class="hms-pill" :class="sepsisPill(s.classification)">{{ s.classification.replace(/_/g,' ') }}</span></td>
                    <td>{{ (s.antibiotic_protocol || 'none').replace(/_/g,' ') }}</td>
                    <td><span class="hms-pill" :class="cultPill(s.blood_culture_result)">{{ s.blood_culture_result }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-muted">No sepsis screens yet.</div>
            </div>
          </div>

          <!-- ============ KMC (KANGAROO MOTHER CARE) ============ -->
          <div class="hms-card mt-3">
            <div class="hms-card-head">
              <span><i class="mdi mdi-baby-face-outline"></i> Kangaroo Mother Care (KMC)</span>
              <button v-if="!detail.KMC" class="hms-btn hms-btn-blue hms-btn-sm" @click="enrolKmc"><i class="mdi mdi-plus"></i> Enrol</button>
              <button v-else class="hms-btn hms-btn-success hms-btn-sm" @click="openKmcSession"><i class="mdi mdi-plus"></i> Log session</button>
            </div>
            <div class="hms-card-body">
              <div class="hms-muted-sm mb-2">Continuous skin-to-skin contact for LBW / preterm babies — the most cost-effective preterm-survival intervention.</div>
              <div v-if="detail.KMC">
                <div class="hms-grid-2">
                  <div>
                    <div><strong>Intent:</strong> {{ detail.KMC.intent.replace(/_/g,' ') }} ({{ detail.KMC.target_hours_per_day }}h/day target)</div>
                    <div><strong>Enrolled:</strong> {{ formatDate(detail.KMC.enrolled_at) }}, weight {{ detail.KMC.enrolled_weight_g }}g</div>
                  </div>
                  <div>
                    <div><strong>Status:</strong> <span class="hms-pill hms-pill-info">{{ detail.KMC.status.replace(/_/g,' ') }}</span></div>
                    <div><strong>Sessions logged:</strong> {{ (detail.KmcSessions || []).length }} ({{ totalKmcHours }}h total)</div>
                  </div>
                </div>
                <table class="hms-table mt-2" v-if="(detail.KmcSessions || []).length">
                  <thead><tr><th>Date</th><th>Start</th><th>End</th><th>Mins</th><th>Provider</th><th>Temp</th><th>SpO2</th></tr></thead>
                  <tbody>
                    <tr v-for="s in detail.KmcSessions.slice(-10).reverse()" :key="s.session_id">
                      <td>{{ formatDate(s.session_date) }}</td>
                      <td>{{ formatTime(s.session_start) }}</td>
                      <td>{{ formatTime(s.session_end) || '—' }}</td>
                      <td><strong>{{ s.duration_minutes || '—' }}</strong></td>
                      <td>{{ s.provider }}</td>
                      <td>{{ s.baby_temperature || '—' }}</td>
                      <td>{{ s.baby_spo2 ? s.baby_spo2 + '%' : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="hms-muted">Not enrolled in KMC. Click "Enrol" if baby is LBW (&lt;2500g).</div>
            </div>
          </div>

          <!-- ============ PHOTOTHERAPY ============ -->
          <div class="hms-card mt-3">
            <div class="hms-card-head">
              <span><i class="mdi mdi-lightbulb-on"></i> Phototherapy (for neonatal jaundice)</span>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openPhoto"><i class="mdi mdi-plus"></i> Record</button>
            </div>
            <div class="hms-card-body">
              <div class="hms-muted-sm mb-2">Blue-light therapy that breaks down bilirubin in the skin — treats jaundice and prevents brain damage.</div>
              <table class="hms-table" v-if="(detail.Phototherapy || []).length">
                <thead><tr><th>Started</th><th>Duration</th><th>Type</th><th>Indication</th><th>Start bili</th><th>End bili</th><th>Peak</th><th>Outcome</th></tr></thead>
                <tbody>
                  <tr v-for="p in detail.Phototherapy" :key="p.pt_id" :class="p.exchange_transfusion_done==1 || p.outcome==='escalated_exchange' ? 'row-action' : ''">
                    <td>{{ formatDt(p.started_at) }}</td>
                    <td>{{ p.duration_hours ? p.duration_hours + 'h' : '—' }}</td>
                    <td>{{ p.phototherapy_type.replace(/_/g,' ') }}</td>
                    <td class="hms-muted-sm">{{ p.indication.replace(/_/g,' ') }}</td>
                    <td>{{ p.start_total_bili || '—' }}</td>
                    <td>{{ p.end_total_bili || '—' }}</td>
                    <td><strong :class="p.peak_total_bili >= 20 ? 'hms-bad' : ''">{{ p.peak_total_bili || '—' }}</strong></td>
                    <td><span class="hms-pill" :class="photoOutcomePill(p.outcome)">{{ p.outcome.replace(/_/g,' ') }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-muted">No phototherapy episodes.</div>
            </div>
          </div>

          <!-- Modals for the 4 new sections -->
          <div v-if="respModal" class="hms-modal-bg" @click.self="respModal=false">
            <div class="hms-modal">
              <div class="hms-modal-head"><h5>Respiratory support change</h5><button class="hms-btn hms-btn-secondary" @click="respModal=false">&times;</button></div>
              <div class="hms-modal-body">
                <div class="hms-form-grid">
                  <label>When <input type="datetime-local" v-model="respForm.recorded_at" class="hms-input" /></label>
                  <label>Mode
                    <select v-model="respForm.support_mode" class="hms-input">
                      <option value="room_air">Room air</option>
                      <option value="nasal_prong_O2">Nasal-prong O2</option>
                      <option value="head_box_O2">Head-box O2</option>
                      <option value="HFNC">HFNC</option>
                      <option value="CPAP">CPAP</option>
                      <option value="BiPAP">BiPAP</option>
                      <option value="SIMV">SIMV</option>
                      <option value="SIPPV">SIPPV</option>
                      <option value="HFOV">HFOV</option>
                      <option value="intubated">Intubated</option>
                    </select>
                  </label>
                  <label>FiO2 % <input type="number" v-model="respForm.fio2_pct" class="hms-input" /></label>
                  <label>Flow L/min <input type="number" step="0.1" v-model="respForm.flow_lpm" class="hms-input" /></label>
                  <label>PEEP <input type="number" step="0.1" v-model="respForm.peep_cmH2O" class="hms-input" /></label>
                  <label>PIP <input type="number" step="0.1" v-model="respForm.pip_cmH2O" class="hms-input" /></label>
                  <label>Vent rate /min <input type="number" v-model="respForm.rate_per_min" class="hms-input" /></label>
                  <label>SpO2 % <input type="number" v-model="respForm.spo2_pct" class="hms-input" /></label>
                  <label>Work of breathing
                    <select v-model="respForm.work_of_breathing" class="hms-input">
                      <option value="none">None</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option>
                    </select>
                  </label>
                  <label class="hms-check"><input type="checkbox" v-model="respForm.surfactant_given" :true-value="1" :false-value="0" /> Surfactant given</label>
                  <label class="hms-check"><input type="checkbox" v-model="respForm.abg_done" :true-value="1" :false-value="0" /> ABG done</label>
                  <label v-if="respForm.abg_done==1">pH <input type="number" step="0.01" v-model="respForm.abg_ph" class="hms-input" /></label>
                  <label v-if="respForm.abg_done==1">pCO2 <input type="number" step="0.1" v-model="respForm.abg_pco2" class="hms-input" /></label>
                  <label v-if="respForm.abg_done==1">pO2 <input type="number" step="0.1" v-model="respForm.abg_po2" class="hms-input" /></label>
                </div>
                <label class="d-block mt-2">Notes <textarea v-model="respForm.notes" rows="2" class="hms-input"></textarea></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="respModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" @click="saveResp">Save</button>
              </div>
            </div>
          </div>

          <div v-if="sepsisModal" class="hms-modal-bg" @click.self="sepsisModal=false">
            <div class="hms-modal">
              <div class="hms-modal-head"><h5>Newborn Sepsis Screen</h5><button class="hms-btn hms-btn-secondary" @click="sepsisModal=false">&times;</button></div>
              <div class="hms-modal-body">
                <div class="hms-muted-sm mb-2">Score: +2 per maternal risk factor, +1 per neonate sign, +2 per abnormal lab. ≥6 → suspected sepsis (start antibiotics).</div>
                <h6>Maternal risk factors</h6>
                <div class="hms-form-grid">
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.maternal_pyrexia" :true-value="1" :false-value="0" /> Maternal pyrexia / fever</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.rom_over_18h" :true-value="1" :false-value="0" /> ROM &gt;18h</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.foul_smelling_liquor" :true-value="1" :false-value="0" /> Foul-smelling liquor</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.maternal_uti_or_chorio" :true-value="1" :false-value="0" /> Maternal UTI / chorio</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.untreated_gbs" :true-value="1" :false-value="0" /> Untreated GBS</label>
                </div>
                <h6 class="mt-2">Neonate clinical signs</h6>
                <div class="hms-form-grid">
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.feeding_difficulty" :true-value="1" :false-value="0" /> Feeding difficulty</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.lethargy" :true-value="1" :false-value="0" /> Lethargy</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.poor_perfusion" :true-value="1" :false-value="0" /> Poor perfusion</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.temperature_instability" :true-value="1" :false-value="0" /> Temp instability</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.apnoea" :true-value="1" :false-value="0" /> Apnoea</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.convulsions" :true-value="1" :false-value="0" /> Convulsions</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.jaundice_under_24h" :true-value="1" :false-value="0" /> Jaundice &lt;24h</label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.bulging_fontanelle" :true-value="1" :false-value="0" /> Bulging fontanelle</label>
                </div>
                <h6 class="mt-2">Labs</h6>
                <div class="hms-form-grid">
                  <label>WBC <input type="number" step="0.01" v-model="sepsisForm.wbc_count" class="hms-input" /></label>
                  <label>CRP mg/L <input type="number" step="0.01" v-model="sepsisForm.crp_mg_l" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="sepsisForm.blood_culture_sent" :true-value="1" :false-value="0" /> Blood culture sent</label>
                  <label>Blood culture result
                    <select v-model="sepsisForm.blood_culture_result" class="hms-input">
                      <option value="not_done">Not done</option><option value="pending">Pending</option><option value="sterile">Sterile</option><option value="positive">Positive</option><option value="contaminated">Contaminated</option>
                    </select>
                  </label>
                  <label v-if="sepsisForm.blood_culture_result==='positive'">Organism <input v-model="sepsisForm.blood_culture_organism" class="hms-input" /></label>
                </div>
                <label class="d-block mt-2">Notes <textarea v-model="sepsisForm.notes" rows="2" class="hms-input"></textarea></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="sepsisModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" @click="saveSepsis">Save (auto-scores)</button>
              </div>
            </div>
          </div>

          <div v-if="kmcSessionModal" class="hms-modal-bg" @click.self="kmcSessionModal=false">
            <div class="hms-modal">
              <div class="hms-modal-head"><h5>KMC session</h5><button class="hms-btn hms-btn-secondary" @click="kmcSessionModal=false">&times;</button></div>
              <div class="hms-modal-body">
                <div class="hms-form-grid">
                  <label>Start <input type="datetime-local" v-model="kmcSessionForm.session_start" class="hms-input" /></label>
                  <label>End <input type="datetime-local" v-model="kmcSessionForm.session_end" class="hms-input" /></label>
                  <label>Provider
                    <select v-model="kmcSessionForm.provider" class="hms-input">
                      <option value="mother">Mother</option><option value="father">Father</option><option value="grandmother">Grandmother</option><option value="other_caregiver">Other caregiver</option>
                    </select>
                  </label>
                  <label>Baby temp <input type="number" step="0.1" v-model="kmcSessionForm.baby_temperature" class="hms-input" /></label>
                  <label>Baby SpO2 % <input type="number" v-model="kmcSessionForm.baby_spo2" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="kmcSessionForm.baby_fed_during" :true-value="1" :false-value="0" /> Baby fed during session</label>
                </div>
                <label class="d-block mt-2">Notes <input v-model="kmcSessionForm.notes" class="hms-input" /></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="kmcSessionModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" @click="saveKmcSession">Save</button>
              </div>
            </div>
          </div>

          <div v-if="photoModal" class="hms-modal-bg" @click.self="photoModal=false">
            <div class="hms-modal">
              <div class="hms-modal-head"><h5>Phototherapy episode</h5><button class="hms-btn hms-btn-secondary" @click="photoModal=false">&times;</button></div>
              <div class="hms-modal-body">
                <div class="hms-form-grid">
                  <label>Started <input type="datetime-local" v-model="photoForm.started_at" class="hms-input" /></label>
                  <label>Ended (leave blank if ongoing) <input type="datetime-local" v-model="photoForm.ended_at" class="hms-input" /></label>
                  <label>Type
                    <select v-model="photoForm.phototherapy_type" class="hms-input">
                      <option value="single_light">Single light</option>
                      <option value="double_light">Double light</option>
                      <option value="intensive">Intensive</option>
                      <option value="exchange_transfusion_required">Exchange transfusion required</option>
                    </select>
                  </label>
                  <label>Indication
                    <select v-model="photoForm.indication" class="hms-input">
                      <option value="jaundice_clinical">Clinical jaundice</option>
                      <option value="total_bilirubin_elevated">Total bilirubin elevated</option>
                      <option value="direct_bilirubin_elevated">Direct bilirubin elevated</option>
                      <option value="prophylactic_preterm">Prophylactic (preterm)</option>
                    </select>
                  </label>
                  <label>Age at start (h) <input type="number" v-model="photoForm.age_at_start_hours" class="hms-input" /></label>
                  <label>Start total bili (mg/dL) <input type="number" step="0.01" v-model="photoForm.start_total_bili" class="hms-input" /></label>
                  <label>End total bili <input type="number" step="0.01" v-model="photoForm.end_total_bili" class="hms-input" /></label>
                  <label>Peak total bili <input type="number" step="0.01" v-model="photoForm.peak_total_bili" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="photoForm.exchange_transfusion_done" :true-value="1" :false-value="0" /> Exchange transfusion done</label>
                  <label>Outcome
                    <select v-model="photoForm.outcome" class="hms-input">
                      <option value="continued">Continued</option><option value="resolved">Resolved</option><option value="escalated_exchange">Escalated to exchange</option><option value="transferred">Transferred</option>
                    </select>
                  </label>
                </div>
                <label class="d-block mt-2">Notes <input v-model="photoForm.notes" class="hms-input" /></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="photoModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" @click="savePhoto">Save</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ INDICATORS ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">NICU activity ({{ indicatorPeriod.replace('_',' ') }})</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Admissions</span><strong>{{ indicators.admissions_period || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Discharged</span><strong>{{ indicators.discharged_period || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Died</span><strong>{{ indicators.died_period || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Very/Extremely preterm</span><strong>{{ indicators.very_preterm || 0 }}</strong></div>
              <div class="hms-ind-row"><span>VLBW / ELBW</span><strong>{{ indicators.vlbw_elbw || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Avg length of stay</span><strong>{{ indicators.avg_los_days ? Number(indicators.avg_los_days).toFixed(1) + ' d' : '—' }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Neonatal mortality rate</div>
            <div class="hms-card-body">
              <div class="hms-suppression">
                <div class="hms-supp-label">Died / Admissions</div>
                <div class="hms-supp-bar"><div class="hms-supp-fill hms-supp-fill-red" :style="{ width: Math.min(100, mortalityPct) + '%' }"></div></div>
                <div class="hms-supp-num">{{ mortalityPct }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =============== ADMIT MODAL =============== -->
    <div v-if="admitModal" class="hms-modal-bg" @click.self="admitModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Admit Baby to NICU</h5><button class="hms-icon-btn" @click="admitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">If the baby's patient record doesn't exist, leave Patient ID blank — the system will create one from the details below.</div>
          <div class="row g-2">
            <div class="col-12" v-if="!admit.patient_id">
              <label class="hms-label">Existing baby (optional)</label>
              <div class="d-flex gap-2">
                <input v-model="admit.patient_search" class="hms-input" placeholder="Search baby by patient # or name" @keyup.enter="findAdmitPatient" />
                <button class="hms-btn hms-btn-blue" @click="findAdmitPatient">Find</button>
              </div>
            </div>
            <div v-else class="col-12 hms-patient-chip">
              <i class="mdi mdi-baby-face"></i> {{ admit.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="admit.patient_id=null">change</a>
            </div>
            <template v-if="!admit.patient_id">
              <div class="col-4"><label class="hms-label">Baby first name</label><input v-model="admit.baby_first_name" class="hms-input" placeholder="Baby" /></div>
              <div class="col-4"><label class="hms-label">Baby surname</label><input v-model="admit.baby_last_name" class="hms-input" placeholder="of [Mother surname]" /></div>
              <div class="col-2"><label class="hms-label">Sex</label>
                <select v-model="admit.baby_sex" class="hms-input"><option value="M">M</option><option value="F">F</option><option value="ambiguous">Amb</option></select>
              </div>
              <div class="col-2"><label class="hms-label">DOB</label><input v-model="admit.baby_dob" type="date" class="hms-input" /></div>
            </template>
            <div class="col-12"><label class="hms-label">Mother (existing patient)</label>
              <div class="d-flex gap-2">
                <input v-model="admit.mother_search" class="hms-input" placeholder="Mother name / patient # / phone" @keyup.enter="findMother" />
                <button class="hms-btn hms-btn-blue" @click="findMother">Find</button>
              </div>
              <div v-if="admit.mother_patient_id" class="hms-muted-sm mt-1">Linked: {{ admit.mother_name }}</div>
            </div>
            <div class="col-4"><label class="hms-label">Admission date *</label><input v-model="admit.admission_date" type="datetime-local" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Source</label>
              <select v-model="admit.admission_source" class="hms-input">
                <option value="inborn">Inborn</option>
                <option value="outborn">Outborn</option>
                <option value="transfer_in">Transfer in</option>
                <option value="readmission">Readmission</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Care level</label>
              <select v-model="admit.care_level" class="hms-input">
                <option value="level_1">Level 1 — Basic</option>
                <option value="level_2">Level 2 — Special care</option>
                <option value="level_3">Level 3 — Intensive</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Birth weight (g)</label><input v-model.number="admit.admission_weight_g" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Gestation (wks)</label><input v-model.number="admit.gestation_weeks" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">APGAR 1'</label><input v-model.number="admit.apgar_1" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">APGAR 5'</label><input v-model.number="admit.apgar_5" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Admission diagnosis</label><textarea v-model="admit.admission_diagnosis" class="hms-input" rows="2" placeholder="prematurity / RDS / NNS / asphyxia / MAS / HIE / jaundice…"></textarea></div>
            <div class="col-4"><label class="hms-label">Cot #</label><input v-model="admit.cot_number" class="hms-input" /></div>
            <div class="col-8"><label class="hms-label">Notes</label><input v-model="admit.notes" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="admitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveAdmit">{{ saving?'Admitting…':'Admit' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== PROGRESS MODAL =============== -->
    <div v-if="progressModal" class="hms-modal-bg" @click.self="progressModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>Progress Note — {{ detail.admission && detail.admission.baby_name }}</h5><button class="hms-icon-btn" @click="progressModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Assessed at *</label><input v-model="progress.assessed_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Weight (g)</label><input v-model.number="progress.weight_g" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Length (cm)</label><input v-model.number="progress.length_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Head circ (cm)</label><input v-model.number="progress.head_circumference_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-12"><div class="hms-section-label">VITALS</div></div>
            <div class="col-2"><label class="hms-label">T °C</label><input v-model.number="progress.temperature" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">HR</label><input v-model.number="progress.heart_rate" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">RR</label><input v-model.number="progress.respiratory_rate" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">SpO₂%</label><input v-model.number="progress.spo2" type="number" min="0" max="100" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Glucose</label><input v-model.number="progress.blood_glucose_mmol" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Apnea/24h</label><input v-model.number="progress.apnea_episodes_24h" type="number" class="hms-input" /></div>
            <div class="col-12"><div class="hms-section-label">FEEDING</div></div>
            <div class="col-3"><label class="hms-label">Method</label>
              <select v-model="progress.feeding_method" class="hms-input">
                <option value="breast">Breast</option>
                <option value="cup">Cup</option>
                <option value="syringe">Syringe</option>
                <option value="NGT">NGT</option>
                <option value="OGT">OGT</option>
                <option value="expressed_breast_milk">EBM</option>
                <option value="formula">Formula</option>
                <option value="iv_fluids">IV fluids</option>
                <option value="npo">NPO</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Volume (ml)</label><input v-model.number="progress.feed_volume_ml" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Frequency (hr)</label><input v-model.number="progress.feed_frequency_hr" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">IV fluids (ml)</label><input v-model.number="progress.iv_fluids_ml" type="number" class="hms-input" /></div>
            <div class="col-12"><div class="hms-section-label">RESPIRATORY SUPPORT</div></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.on_oxygen" :true-value="1" :false-value="0" /> On O₂</label></div>
            <div class="col-2"><label class="hms-label">L/min</label><input v-model.number="progress.oxygen_lpm" type="number" step="0.1" class="hms-input" :disabled="!progress.on_oxygen" /></div>
            <div class="col-2"><label class="hms-label">FiO₂ %</label><input v-model.number="progress.fio2_pct" type="number" class="hms-input" :disabled="!progress.on_oxygen" /></div>
            <div class="col-2 d-flex align-items-end"><label><input type="checkbox" v-model="progress.on_cpap" :true-value="1" :false-value="0" /> CPAP</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.on_ventilator" :true-value="1" :false-value="0" /> Ventilator</label></div>
            <div class="col-12"><div class="hms-section-label">JAUNDICE / KMC / SEPSIS</div></div>
            <div class="col-3"><label class="hms-label">Bilirubin (mg/dL)</label><input v-model.number="progress.bilirubin_total" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.on_phototherapy" :true-value="1" :false-value="0" /> Phototherapy</label></div>
            <div class="col-3"><label class="hms-label">Photo hours</label><input v-model.number="progress.phototherapy_hours" type="number" class="hms-input" :disabled="!progress.on_phototherapy" /></div>
            <div class="col-3"><label class="hms-label">KMC hours</label><input v-model.number="progress.kmc_hours" type="number" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.sepsis_screen_done" :true-value="1" :false-value="0" /> Sepsis screen</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.sepsis_screen_pos" :true-value="1" :false-value="0" /> Positive</label></div>
            <div class="col-3"><label class="hms-label">CRP (mg/L)</label><input v-model.number="progress.crp_mg_l" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="progress.seizures_today" :true-value="1" :false-value="0" /> Seizures</label></div>
            <div class="col-12"><label class="hms-label">Drugs given</label><input v-model="progress.drugs_given" class="hms-input" placeholder="Ampi/Genta x14d, Caffeine, Vit K…" /></div>
            <div class="col-12"><label class="hms-label">Assessment notes</label><textarea v-model="progress.assessment_notes" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Plan</label><textarea v-model="progress.plan" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="progressModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveProgress">{{ saving?'Saving…':'Save Note' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== DISCHARGE MODAL =============== -->
    <div v-if="dischargeModal" class="hms-modal-bg" @click.self="dischargeModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>Discharge / Outcome</h5><button class="hms-icon-btn" @click="dischargeModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Outcome *</label>
              <select v-model="discharge.status" class="hms-input">
                <option value="discharged">Discharged home</option>
                <option value="transferred_out">Transferred out</option>
                <option value="died">Died</option>
                <option value="absconded">Absconded</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Discharge weight (g)</label><input v-model.number="discharge.discharge_weight_g" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Discharge diagnosis</label><textarea v-model="discharge.discharge_diagnosis" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Outcome notes</label><textarea v-model="discharge.outcome_notes" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Follow-up date</label><input v-model="discharge.follow_up_date" type="date" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="dischargeModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveDischarge">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "NicuManagement",
  components: { Navigation },
  data() {
    return {
      tab: 'census', saving: false,
      stats: {}, occupancy: [],
      detail: { admission: null, progress: [] },

      indicatorPeriod: 'this_month',
      indicators: {}, mortalityPct: 0,

      admitModal: false, admit: this.blankAdmit(),
      progressModal: false, progress: this.blankProgress(),
      dischargeModal: false, discharge: this.blankDischarge(),
      // Deepening
      respModal: false, respForm: this.blankResp(),
      sepsisModal: false, sepsisForm: this.blankSepsis(),
      kmcSessionModal: false, kmcSessionForm: this.blankKmcSession(),
      photoModal: false, photoForm: this.blankPhoto()
    };
  },
  computed: {
    weightPoints() {
      return (this.detail.progress || [])
        .filter(p => p.weight_g != null && p.day_of_life != null)
        .map(p => ({ dol: parseInt(p.day_of_life), w: parseInt(p.weight_g) }));
    },
    weightYTicks() {
      if (!this.weightPoints.length) return [];
      const min = Math.min(...this.weightPoints.map(p => p.w));
      const max = Math.max(...this.weightPoints.map(p => p.w));
      const lo = Math.floor(min / 250) * 250;
      const hi = Math.ceil(max / 250) * 250;
      const ticks = [];
      for (let g = lo; g <= hi; g += 250) ticks.push(g);
      return ticks;
    },
    totalKmcHours() {
      const mins = (this.detail.KmcSessions || []).reduce((s, x) => s + (Number(x.duration_minutes) || 0), 0);
      return Math.round(mins / 60 * 10) / 10;
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankAdmit() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return {
        patient_id: null, patient_name: '', patient_search: '',
        baby_first_name: 'Baby', baby_last_name: '', baby_sex: 'M',
        baby_dob: now.toISOString().substring(0,10),
        mother_patient_id: null, mother_name: '', mother_search: '',
        admission_date: localDt, admission_source: 'inborn',
        admission_weight_g: null, gestation_weeks: null,
        apgar_1: null, apgar_5: null,
        admission_diagnosis: '', care_level: 'level_2',
        cot_number: '', notes: ''
      };
    },
    blankProgress() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return {
        assessed_at: localDt,
        weight_g: null, length_cm: null, head_circumference_cm: null,
        temperature: null, heart_rate: null, respiratory_rate: null, spo2: null, blood_glucose_mmol: null,
        feeding_method: 'breast', feed_volume_ml: null, feed_frequency_hr: 3, iv_fluids_ml: null,
        urine_passed: 1, urine_volume_ml: null, stool_passed: 1,
        on_oxygen: 0, oxygen_lpm: null, fio2_pct: null, on_cpap: 0, on_ventilator: 0,
        apnea_episodes_24h: 0, desat_episodes_24h: 0,
        bilirubin_total: null, on_phototherapy: 0, phototherapy_hours: 0, kmc_hours: 0,
        sepsis_screen_done: 0, sepsis_screen_pos: 0, crp_mg_l: null, seizures_today: 0,
        drugs_given: '', assessment_notes: '', plan: ''
      };
    },
    blankDischarge() { return { status: 'discharged', discharge_weight_g: null, discharge_diagnosis: '', outcome_notes: '', follow_up_date: '' }; },

    async loadAll() {
      try {
        const r = await executeJson({ function: 'getNicuOccupancy' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.occupancy = d.Occupancy || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openAdmitModal() { this.admit = this.blankAdmit(); this.admitModal = true; },
    async findAdmitPatient() {
      const q = (this.admit.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.admit.patient_id = p.patient_id;
        this.admit.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async findMother() {
      const q = (this.admit.mother_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No mother found.'); return; }
        this.admit.mother_patient_id = p.patient_id;
        this.admit.mother_name = p.first_name + ' ' + p.last_name;
        if (!this.admit.baby_last_name && p.last_name) this.admit.baby_last_name = 'of ' + p.last_name;
      }
    },
    async saveAdmit() {
      this.saving = true;
      try {
        const params = { function: 'admitToNicu', ...this.admit };
        delete params.patient_search; delete params.patient_name; delete params.mother_search; delete params.mother_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.admitModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(b) {
      try {
        const r = await executeJson({ function: 'getNicuAdmissionDetail', nicu_admission_id: b.nicu_admission_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail.admission = d.Admission;
          this.detail.progress = d.Progress || [];
          this.detail.Respiratory = d.Respiratory || [];
          this.detail.Sepsis = d.Sepsis || [];
          this.detail.KMC = d.KMC || null;
          this.detail.KmcSessions = d.KmcSessions || [];
          this.detail.Phototherapy = d.Phototherapy || [];
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },
    async refreshDetail() {
      if (!this.detail.admission) return;
      await this.openDetail({ nicu_admission_id: this.detail.admission.nicu_admission_id });
    },
    openProgressFor(b) { this.openDetail(b).then(() => this.openProgressModal()); },
    openProgressModal() { this.progress = this.blankProgress(); this.progressModal = true; },
    async saveProgress() {
      if (!this.detail.admission) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordNicuProgress', nicu_admission_id: this.detail.admission.nicu_admission_id, ...this.progress }, hms);
        if (r.data && r.data.success) { this.progressModal = false; await this.openDetail(this.detail.admission); await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openDischargeModal() { this.discharge = this.blankDischarge(); this.dischargeModal = true; },
    async saveDischarge() {
      if (!this.detail.admission) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'dischargeFromNicu', nicu_admission_id: this.detail.admission.nicu_admission_id, ...this.discharge }, hms);
        if (r.data && r.data.success) { this.dischargeModal = false; this.tab = 'census'; this.detail.admission = null; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getNicuStats', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) {
          this.indicators = (r.data.data && r.data.data.Stats) || {};
          this.mortalityPct = (r.data.data && r.data.data.MortalityPct) || 0;
        }
      } catch (e) { console.error(e); }
    },

    // ==== chart helpers ====
    xDol(dol) { return 40 + Math.min(600, (dol || 0) * 18); },
    yWt(g) {
      if (!this.weightYTicks.length) return 100;
      const lo = this.weightYTicks[0];
      const hi = this.weightYTicks[this.weightYTicks.length - 1];
      const range = Math.max(1, hi - lo);
      return 170 - ((g - lo) / range) * 150;
    },

    // ==== formatting ====
    cardClass(b) {
      if (b.ever_sepsis == 1 || b.cpap_24h == 1) return 'st-action';
      if (['very_preterm','extremely_preterm'].includes(b.prematurity_class) || ['very_low','extremely_low'].includes(b.birth_weight_class)) return 'st-warn';
      return '';
    },
    careLevelPill(l) { return ({ level_1: 'hms-pill-info', level_2: 'hms-pill-warn', level_3: 'hms-pill-danger' })[l] || 'hms-pill-muted'; },
    formatCareLevel(l) { return ({ level_1: 'L1 Basic', level_2: 'L2 Special', level_3: 'L3 Intensive' })[l] || l; },
    formatPrem(p) {
      return ({ term: 'Term', late_preterm: 'Late preterm (34-36w)', moderate_preterm: 'Mod preterm (32-33w)',
                very_preterm: 'Very preterm (28-31w)', extremely_preterm: 'Extremely preterm (<28w)',
                post_term: 'Post-term (≥42w)' })[p] || p;
    },
    formatBw(b) {
      return ({ normal: 'Normal BW', low: 'LBW', very_low: 'VLBW (<1500g)',
                extremely_low: 'ELBW (<1000g)', macrosomic: 'Macrosomic (>4000g)' })[b] || b;
    },
    formatFeeding(f) {
      return ({ breast: 'Breast', cup: 'Cup', syringe: 'Syringe', NGT: 'NGT', OGT: 'OGT',
                expressed_breast_milk: 'EBM', formula: 'Formula', iv_fluids: 'IV', npo: 'NPO', mixed: 'Mixed' })[f] || f;
    },
    progressRowClass(p) {
      if (p.sepsis_screen_pos == 1 || p.seizures_today == 1) return 'row-action';
      if (p.on_ventilator == 1) return 'row-action';
      if (p.on_cpap == 1) return 'row-warn';
      return '';
    },
    formatRelative(s) {
      if (!s) return '—';
      const d = new Date(s.replace ? s.replace(' ', 'T') : s);
      if (isNaN(d)) return s;
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return mins + 'm ago';
      const h = Math.floor(mins / 60);
      if (h < 24) return h + 'h ago';
      return Math.floor(h / 24) + 'd ago';
    },
    truncate(s, n) { return (s || '').length > n ? (s || '').substring(0, n) + '…' : (s || ''); },

    // ===== DEEPENING: Respiratory · Sepsis · KMC · Phototherapy =====
    blankResp() { return { recorded_at: new Date().toISOString().substring(0,16),
      support_mode: 'room_air', fio2_pct: 21, flow_lpm: null, peep_cmH2O: null, pip_cmH2O: null, rate_per_min: null,
      spo2_pct: null, work_of_breathing: 'none',
      abg_done: 0, abg_ph: null, abg_pco2: null, abg_po2: null, abg_hco3: null,
      surfactant_given: 0, notes: '' }; },
    openResp() { this.respForm = this.blankResp(); this.respModal = true; },
    async saveResp() {
      const payload = { function:'recordNicuRespSupport', nicu_admission_id: this.detail.admission.nicu_admission_id, ...this.respForm };
      const r = await executeJson(payload, hms);
      if (r.data && r.data.success) { this.respModal = false; await this.refreshDetail(); }
      else if (r.data) alert(r.data.message || 'Failed.');
    },
    respModePill(m) { return ({ room_air:'hms-pill-success', nasal_prong_O2:'hms-pill-info', HFNC:'hms-pill-warn', CPAP:'hms-pill-warn', BiPAP:'hms-pill-warn', SIMV:'hms-pill-danger', SIPPV:'hms-pill-danger', HFOV:'hms-pill-danger', intubated:'hms-pill-danger' })[m] || 'hms-pill-muted'; },

    blankSepsis() { return {
      maternal_pyrexia:0, rom_over_18h:0, foul_smelling_liquor:0, maternal_uti_or_chorio:0, untreated_gbs:0,
      feeding_difficulty:0, lethargy:0, poor_perfusion:0, temperature_instability:0, apnoea:0, convulsions:0, jaundice_under_24h:0, bulging_fontanelle:0,
      wbc_count:null, crp_mg_l:null,
      blood_culture_sent:0, blood_culture_result:'not_done', blood_culture_organism:'', csf_done:0, csf_result:'not_done',
      notes:'' }; },
    openSepsis() { this.sepsisForm = this.blankSepsis(); this.sepsisModal = true; },
    async saveSepsis() {
      const payload = { function:'recordNicuSepsisScreen', nicu_admission_id: this.detail.admission.nicu_admission_id, ...this.sepsisForm };
      const r = await executeJson(payload, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        alert('Sepsis screen saved.\nScore: ' + d.risk_score + '\nClassification: ' + d.classification + '\nRecommended protocol: ' + d.antibiotic_protocol);
        this.sepsisModal = false;
        await this.refreshDetail();
      } else if (r.data) alert(r.data.message || 'Failed.');
    },
    sepsisPill(c) { return ({ low_risk:'hms-pill-success', observation:'hms-pill-info', suspected_sepsis:'hms-pill-warn', confirmed_sepsis:'hms-pill-danger' })[c] || 'hms-pill-muted'; },
    cultPill(r) { return ({ not_done:'hms-pill-muted', pending:'hms-pill-info', sterile:'hms-pill-success', positive:'hms-pill-danger', contaminated:'hms-pill-warn' })[r] || 'hms-pill-muted'; },

    async enrolKmc() {
      const r = await executeJson({ function:'enrolKmc', nicu_admission_id: this.detail.admission.nicu_admission_id, intent:'intermittent', target_hours_per_day:16 }, hms);
      if (r.data && r.data.success) await this.refreshDetail();
      else if (r.data) alert(r.data.message || 'Failed.');
    },
    blankKmcSession() { return { session_start: new Date().toISOString().substring(0,16), session_end: '', provider: 'mother', baby_temperature: null, baby_spo2: null, baby_fed_during: 0, notes: '' }; },
    openKmcSession() { this.kmcSessionForm = this.blankKmcSession(); this.kmcSessionModal = true; },
    async saveKmcSession() {
      if (!this.detail.KMC) { alert('Enrol first.'); return; }
      const r = await executeJson({ function:'recordKmcSession', kmc_id: this.detail.KMC.kmc_id, ...this.kmcSessionForm }, hms);
      if (r.data && r.data.success) { this.kmcSessionModal = false; await this.refreshDetail(); }
      else if (r.data) alert(r.data.message || 'Failed.');
    },

    blankPhoto() { return { started_at: new Date().toISOString().substring(0,16), ended_at: '',
      indication: 'jaundice_clinical', age_at_start_hours: null,
      start_total_bili: null, start_direct_bili: null, end_total_bili: null, peak_total_bili: null,
      phototherapy_type: 'single_light', iv_fluids_given: 0, exchange_transfusion_done: 0, outcome: 'continued', notes: '' }; },
    openPhoto() { this.photoForm = this.blankPhoto(); this.photoModal = true; },
    async savePhoto() {
      const r = await executeJson({ function:'recordNicuPhototherapy', nicu_admission_id: this.detail.admission.nicu_admission_id, ...this.photoForm }, hms);
      if (r.data && r.data.success) { this.photoModal = false; await this.refreshDetail(); }
      else if (r.data) alert(r.data.message || 'Failed.');
    },
    photoOutcomePill(o) { return ({ resolved:'hms-pill-success', continued:'hms-pill-info', escalated_exchange:'hms-pill-danger', transferred:'hms-pill-muted', died:'hms-pill-danger' })[o] || 'hms-pill-muted'; },

    formatTime(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; }
.hms-stat-success { color: #276749; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-baby-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.hms-baby-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; }
.hms-baby-card.st-warn   { border-left-color: #dd6b20; background: #fffaf0; }
.hms-baby-card.st-action { border-left-color: #e53e3e; background: #fff5f5; }
.hms-baby-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 6px; }
.hms-baby-name { font-weight: 600; color: #1a202c; font-size: 15px; }
.hms-baby-meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; font-size: 12px; margin-bottom: 8px; }
.hms-vk { display: inline-block; min-width: 32px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }
.hms-baby-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.hms-tag { padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; }
.hms-tag-warn   { background: #feebc8; color: #7b341e; }
.hms-tag-info   { background: #bee3f8; color: #2a4365; }
.hms-tag-danger { background: #fed7d7; color: #742a2a; }
.hms-baby-dx { font-size: 12px; color: #4a5568; margin-bottom: 4px; }
.hms-baby-actions { display: flex; gap: 6px; margin-top: 8px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 6px 8px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 6px 0 8px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #a0aec0; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-suppression { margin-top: 14px; }
.hms-supp-label  { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-supp-bar    { background: #edf2f7; border-radius: 6px; height: 18px; overflow: hidden; margin: 4px 0; }
.hms-supp-fill   { background: linear-gradient(90deg, #38a169, #276749); height: 100%; }
.hms-supp-fill-red { background: linear-gradient(90deg, #e53e3e, #c53030); }
.hms-supp-num    { text-align: right; font-weight: 700; font-size: 18px; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
